import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ShieldCheck,
  Power,
  KeyRound,
  UserPlus,
  Trash2,
  Pause,
  PlayCircle,
  Copy,
  History,
  CheckCircle2,
  XCircle,
  RotateCcw,
  AlertTriangle,
} from "lucide-react";
import AdminSectionsTab from "@/components/admin/AdminSectionsTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function authHeaders() {
  const t = localStorage.getItem("token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export default function AdminSEOAgencyAccess() {
  const [tab, setTab] = useState("accounts");
  return (
    <div data-testid="admin-seo-agency-access">
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-[#0F172A] flex items-center gap-2">
          <ShieldCheck className="w-7 h-7 text-[#F97316]" /> SEO Agency Access
        </h2>
        <p className="text-[#64748B] mt-1 text-sm">
          Oversight controls for the external SEO agency. All edits are auto-scrubbed for Google Ads compliance
          and audit-logged. Edits to top-5 pages require your approval.
        </p>
      </div>

      <div className="flex gap-2 border-b border-[#E2E8F0] mb-6">
        {[
          { id: "accounts", label: "Accounts" },
          { id: "pending", label: "Pending Approval" },
          { id: "content-requests", label: "Content Requests" },
          { id: "sections", label: "New Sections" },
          { id: "audit", label: "Audit Log" },
          { id: "killswitch", label: "Kill Switch" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`seo-agency-tab-${t.id}`}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              tab === t.id ? "border-[#F97316] text-[#0F172A]" : "border-transparent text-[#64748B] hover:text-[#0F172A]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "accounts" && <AccountsTab />}
      {tab === "pending" && <PendingTab />}
      {tab === "content-requests" && <ContentRequestsTab />}
      {tab === "sections" && <AdminSectionsTab />}
      {tab === "audit" && <AuditTab />}
      {tab === "killswitch" && <KillSwitchTab />}
    </div>
  );
}

// ── ACCOUNTS ────────────────────────────────────────────────────────────
function AccountsTab() {
  const [accounts, setAccounts] = useState([]);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [createdCred, setCreatedCred] = useState(null);

  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/admin/seo/accounts`, { headers: authHeaders() });
      setAccounts(r.data || []);
    } catch (e) {
      toast.error("Failed to load accounts");
    }
  };
  useEffect(() => {
    refresh();
  }, []);

  const create = async () => {
    if (!email || !name) {
      toast.error("Email and name required");
      return;
    }
    try {
      const r = await axios.post(
        `${API}/admin/seo/accounts/create`,
        { email, name },
        { headers: authHeaders() }
      );
      setCreatedCred({ email, password: r.data.password_plain });
      setEmail("");
      setName("");
      refresh();
      toast.success("Account created. Copy the password — shown only once.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    }
  };

  const suspend = async (id, isActive) => {
    try {
      await axios.post(
        `${API}/admin/seo/accounts/${id}/update`,
        { is_active: !isActive },
        { headers: authHeaders() }
      );
      toast.success(isActive ? "Account suspended" : "Account reactivated");
      refresh();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const reset = async (id, accEmail) => {
    if (!window.confirm(`Reset password for ${accEmail}? Old sessions will expire within 60s.`)) return;
    try {
      const r = await axios.post(`${API}/admin/seo/accounts/${id}/reset-password`, {}, { headers: authHeaders() });
      setCreatedCred({ email: accEmail, password: r.data.new_password });
      toast.success("Password reset. Copy and share securely.");
    } catch (e) {
      toast.error("Reset failed");
    }
  };

  const del = async (id, accEmail) => {
    if (!window.confirm(`Permanently delete account ${accEmail}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/seo/accounts/${id}`, { headers: authHeaders() });
      toast.success("Account deleted");
      refresh();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <UserPlus className="w-4 h-4" /> Create SEO Editor Account
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Input
            placeholder="Email (e.g. seo@speicher.in)"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="seo-new-account-email"
          />
          <Input
            placeholder="Full name (e.g. Speicher SEO Team)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="seo-new-account-name"
          />
          <Button onClick={create} className="bg-[#F97316] hover:bg-[#EA7515] text-white" data-testid="seo-new-account-create">
            Create Account
          </Button>
        </CardContent>
      </Card>

      {createdCred && (
        <Card className="border-green-300 bg-green-50">
          <CardContent className="pt-6 space-y-3">
            <div className="flex items-center gap-2 text-green-800 font-semibold">
              <KeyRound className="w-4 h-4" /> Credentials — copy now, shown only once
            </div>
            <div className="bg-white border border-green-200 rounded p-3 font-mono text-sm space-y-1">
              <div>
                <span className="text-[#64748B]">Email:</span> <strong>{createdCred.email}</strong>
              </div>
              <div className="flex items-center justify-between">
                <span>
                  <span className="text-[#64748B]">Password:</span> <strong>{createdCred.password}</strong>
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard.writeText(createdCred.password);
                    toast.success("Password copied");
                  }}
                  data-testid="seo-copy-password"
                >
                  <Copy className="w-3 h-3 mr-1" /> Copy
                </Button>
              </div>
              <div className="text-xs text-[#64748B] pt-1">
                Login URL: <code>/seo-admin</code>
              </div>
            </div>
            <Button size="sm" variant="ghost" onClick={() => setCreatedCred(null)}>
              Dismiss
            </Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Active Accounts ({accounts.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {accounts.length === 0 ? (
            <p className="text-sm text-[#94A3B8]">No SEO editor accounts yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-[#E2E8F0] text-xs uppercase text-[#64748B]">
                  <th className="py-2">Editor</th>
                  <th>Agency</th>
                  <th>Status</th>
                  <th>Edits</th>
                  <th>Last Login</th>
                  <th>Created</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((a) => (
                  <tr key={a.id} className="border-b border-[#F1F5F9]" data-testid={`seo-account-row-${a.id}`}>
                    <td className="py-2">
                      <div className="font-medium">{a.name}</div>
                      <div className="text-xs text-[#64748B]">{a.email}</div>
                    </td>
                    <td>{a.agency || "—"}</td>
                    <td>
                      {a.is_active ? (
                        <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Active</Badge>
                      ) : (
                        <Badge variant="outline" className="text-red-700 border-red-300">
                          Suspended
                        </Badge>
                      )}
                    </td>
                    <td>{a.edits_count || 0}</td>
                    <td className="text-xs">{a.last_login ? new Date(a.last_login).toLocaleString() : "Never"}</td>
                    <td className="text-xs">{new Date(a.created_at).toLocaleDateString()}</td>
                    <td className="text-right space-x-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => suspend(a.id, a.is_active)}
                        data-testid={`seo-account-suspend-${a.id}`}
                      >
                        {a.is_active ? <Pause className="w-3 h-3" /> : <PlayCircle className="w-3 h-3" />}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => reset(a.id, a.email)}
                        data-testid={`seo-account-reset-${a.id}`}
                      >
                        <KeyRound className="w-3 h-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => del(a.id, a.email)}
                        className="text-red-600 hover:bg-red-50"
                        data-testid={`seo-account-delete-${a.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── PENDING APPROVAL ────────────────────────────────────────────────────
function PendingTab() {
  const [pending, setPending] = useState([]);

  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/admin/seo/pending`, { headers: authHeaders() });
      setPending(r.data || []);
    } catch (e) {
      toast.error("Failed to load pending changes");
    }
  };
  useEffect(() => {
    refresh();
  }, []);

  const decide = async (id, decision, label) => {
    if (!window.confirm(`${decision === "approve" ? "Approve" : "Reject"} change to ${label}?`)) return;
    try {
      await axios.post(`${API}/admin/seo/pending/${id}/decide`, { decision }, { headers: authHeaders() });
      toast.success(`Change ${decision === "approve" ? "approved & applied" : "rejected"}`);
      refresh();
    } catch (e) {
      toast.error("Decision failed");
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Pending Owner Approval ({pending.length})</CardTitle>
        <p className="text-xs text-[#64748B] mt-1">
          Only edits to the 5 gated pages (Home, Products, About, Contact, /modular-office-workstations-mumbai)
          require approval. Everything else goes live instantly.
        </p>
      </CardHeader>
      <CardContent>
        {pending.length === 0 ? (
          <p className="text-sm text-[#94A3B8] py-6 text-center">No pending changes. You&apos;re all caught up. ✓</p>
        ) : (
          <div className="space-y-4">
            {pending.map((p) => (
              <div key={p.id} className="border border-[#E2E8F0] rounded p-4" data-testid={`seo-pending-${p.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="text-xs text-[#64748B]">
                      Submitted by <strong>{p.editor_name}</strong> ({p.editor_email}) ·{" "}
                      {new Date(p.submitted_at).toLocaleString()}
                    </div>
                    <div className="font-mono text-sm text-[#0F172A] mt-1">{p.resource_id}</div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => decide(p.id, "approve", p.resource_id)}
                      className="bg-green-600 hover:bg-green-700 text-white"
                      data-testid={`seo-pending-approve-${p.id}`}
                    >
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve & Apply
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => decide(p.id, "reject", p.resource_id)}
                      className="border-red-300 text-red-600 hover:bg-red-50"
                      data-testid={`seo-pending-reject-${p.id}`}
                    >
                      <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                    </Button>
                  </div>
                </div>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-[#F8FAFC] text-[#64748B]">
                      <th className="text-left p-2">Field</th>
                      <th className="text-left p-2">Before</th>
                      <th className="text-left p-2">Proposed After</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(p.proposed_after || {})
                      .filter(([k]) => k !== "compliance_replacements")
                      .map(([k, v]) => (
                        <tr key={k} className="border-t border-[#F1F5F9]">
                          <td className="p-2 font-semibold">{k}</td>
                          <td className="p-2 bg-amber-50 break-all">{String(p.before?.[k] ?? "(empty)")}</td>
                          <td className="p-2 bg-green-50 break-all">{String(v)}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
                {(p.proposed_after?.compliance_replacements || []).length > 0 && (
                  <div className="mt-3 text-xs bg-orange-50 border border-orange-200 rounded p-2">
                    <strong className="text-orange-800">
                      Auto-fixes applied during compliance scrub ({p.proposed_after.compliance_replacements.length}):
                    </strong>
                    <ul className="mt-1 list-disc list-inside text-orange-700">
                      {p.proposed_after.compliance_replacements.map((r, i) => (
                        <li key={i}>
                          <code>&quot;{r.original}&quot;</code> → <code>&quot;{r.replacement}&quot;</code>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── AUDIT LOG ───────────────────────────────────────────────────────────
function AuditTab() {
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [filterPage, setFilterPage] = useState("");

  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/admin/seo/audit-log?limit=200`, { headers: authHeaders() });
      setItems(r.data || []);
    } catch (e) {
      toast.error("Failed to load audit log");
    }
  };
  useEffect(() => {
    refresh();
  }, []);

  const rollback = async (auditId, resource) => {
    if (!window.confirm(`Rollback this change to ${resource}? This restores the previous value.`)) return;
    try {
      await axios.post(`${API}/admin/seo/audit/${auditId}/rollback`, {}, { headers: authHeaders() });
      toast.success("Rolled back successfully");
      refresh();
    } catch (e) {
      toast.error("Rollback failed");
    }
  };

  const filtered = filterPage
    ? items.filter((e) => (e.resource_id || "").toLowerCase().includes(filterPage.toLowerCase()))
    : items;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            <History className="w-4 h-4" /> Full Audit Log ({filtered.length}{filterPage ? ` of ${items.length}` : ""})
          </span>
          <Input
            type="text"
            placeholder="Filter by page or resource…"
            value={filterPage}
            onChange={(e) => setFilterPage(e.target.value)}
            className="max-w-xs h-8 text-xs"
            data-testid="audit-filter-input"
          />
        </CardTitle>
        <p className="text-xs text-[#64748B] mt-1">
          Click any row to expand and see the before/after diff. Every edit Speicher makes — gated or not — is captured here.
        </p>
      </CardHeader>
      <CardContent>
        {filtered.length === 0 ? (
          <p className="text-sm text-[#94A3B8]">No SEO edits {filterPage ? "match this filter" : "recorded yet"}.</p>
        ) : (
          <div className="divide-y divide-[#F1F5F9]">
            {filtered.map((e) => {
              const isOpen = expanded === e.id;
              return (
                <div key={e.id} data-testid={`seo-audit-row-${e.id}`}>
                  <button
                    onClick={() => setExpanded(isOpen ? null : e.id)}
                    className="w-full grid grid-cols-12 gap-2 py-3 text-left text-sm hover:bg-[#F8FAFC] px-2 rounded transition-colors"
                  >
                    <div className="col-span-2 text-xs text-[#64748B] whitespace-nowrap">
                      {new Date(e.timestamp).toLocaleString()}
                    </div>
                    <div className="col-span-2 text-xs">
                      <div className="font-medium text-[#0F172A]">{e.editor_name}</div>
                      <div className="text-[#94A3B8]">{e.editor_email}</div>
                    </div>
                    <div className="col-span-1 text-xs uppercase tracking-wide text-[#64748B]">{e.resource_type}</div>
                    <div className="col-span-4 text-xs font-mono break-all text-[#0F172A]">{e.resource_id}</div>
                    <div className="col-span-1 text-xs">{e.action}</div>
                    <div className="col-span-2 flex items-center justify-end gap-1 flex-wrap">
                      {e.gated && (
                        <Badge variant="outline" className="text-amber-700 border-amber-300 text-[10px]">
                          Gated
                        </Badge>
                      )}
                      {(e.compliance_replacements?.length || 0) > 0 && (
                        <Badge variant="outline" className="text-orange-700 border-orange-300 text-[10px]">
                          {e.compliance_replacements.length} auto-fix
                        </Badge>
                      )}
                      <span className="text-[#94A3B8] text-xs ml-1">{isOpen ? "▼" : "▶"}</span>
                    </div>
                  </button>
                  {isOpen && <AuditDiff entry={e} onRollback={() => rollback(e.id, e.resource_id)} />}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Renders the expanded before/after diff for a single audit entry.
function AuditDiff({ entry, onRollback }) {
  const before = entry.before || {};
  const after = entry.after || {};
  // Union of keys, ignoring internal fields
  const ignoredKeys = ["_id", "updated_at", "updated_by", "path", "compliance_replacements"];
  const keys = Array.from(new Set([...Object.keys(before), ...Object.keys(after)])).filter((k) => !ignoredKeys.includes(k));

  const fmt = (v) => {
    if (v === null || v === undefined || v === "") return <em className="text-[#94A3B8]">(empty)</em>;
    if (typeof v === "object") return <code className="text-[10px]">{JSON.stringify(v, null, 2)}</code>;
    return String(v);
  };

  return (
    <div className="pb-4 px-2" data-testid={`audit-diff-${entry.id}`}>
      <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded p-3 space-y-2">
        {keys.length === 0 ? (
          <p className="text-xs text-[#94A3B8]">No diffable fields (e.g. delete actions).</p>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[#64748B] border-b border-[#E2E8F0]">
                <th className="text-left p-2 w-1/4">Field</th>
                <th className="text-left p-2 w-3/8">Before Speicher edit</th>
                <th className="text-left p-2 w-3/8">After Speicher edit</th>
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => {
                const b = before[k];
                const a = after[k];
                const changed = JSON.stringify(b ?? "") !== JSON.stringify(a ?? "");
                return (
                  <tr key={k} className="border-b border-[#F1F5F9]">
                    <td className="p-2 font-semibold align-top">{k}</td>
                    <td className={`p-2 align-top break-words ${changed ? "bg-amber-50" : ""}`}>{fmt(b)}</td>
                    <td className={`p-2 align-top break-words ${changed ? "bg-green-50" : ""}`}>{fmt(a)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {(entry.compliance_replacements || []).length > 0 && (
          <div className="text-xs bg-orange-50 border border-orange-200 rounded p-2">
            <strong className="text-orange-800">
              Auto-fixes applied during compliance scrub ({entry.compliance_replacements.length}):
            </strong>
            <ul className="mt-1 list-disc list-inside text-orange-700">
              {entry.compliance_replacements.map((r, i) => (
                <li key={i}>
                  <code>&quot;{r.original}&quot;</code> → <code>&quot;{r.replacement}&quot;</code>
                </li>
              ))}
            </ul>
          </div>
        )}
        {["page_seo", "image_alt"].includes(entry.resource_type) && entry.action === "update" && (
          <div className="flex justify-end pt-1">
            <Button
              size="sm"
              variant="outline"
              onClick={onRollback}
              data-testid={`seo-audit-rollback-${entry.id}`}
            >
              <RotateCcw className="w-3 h-3 mr-1" /> Rollback to &quot;Before&quot;
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── KILL SWITCH ─────────────────────────────────────────────────────────
function KillSwitchTab() {
  const [busy, setBusy] = useState(false);

  const suspendAll = async () => {
    if (
      !window.confirm(
        "EMERGENCY: This will suspend ALL SEO editor accounts immediately. They will not be able to log in until you reactivate them. Proceed?"
      )
    )
      return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/seo/suspend-all`, {}, { headers: authHeaders() });
      toast.success(`${r.data.count} account(s) suspended`);
    } catch (e) {
      toast.error("Failed to suspend");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border-red-200">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2 text-red-700">
          <AlertTriangle className="w-5 h-5" /> Emergency Kill Switch
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="bg-red-50 border border-red-200 rounded p-4 text-sm text-red-900">
          <strong>One-click suspend ALL SEO editor accounts.</strong> Use this if the agency is making harmful
          changes or you suspect a credential compromise. Pending changes are NOT auto-rejected — review them in
          the Pending Approval tab and reject anything suspicious.
        </div>
        <Button
          onClick={suspendAll}
          disabled={busy}
          className="bg-red-600 hover:bg-red-700 text-white"
          data-testid="seo-suspend-all-btn"
        >
          <Power className="w-4 h-4 mr-2" /> {busy ? "Suspending…" : "Suspend ALL SEO Editor Accounts"}
        </Button>
      </CardContent>
    </Card>
  );
}


// ── CONTENT CHANGE REQUESTS ─────────────────────────────────────────────
function ContentRequestsTab() {
  const [items, setItems] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [notesMap, setNotesMap] = useState({});

  const refresh = async () => {
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/admin/seo/content-requests`, {
          headers: authHeaders(),
          params: filter === "all" ? {} : { status: filter },
        }),
        axios.get(`${API}/admin/seo/content-overrides`, { headers: authHeaders() }),
      ]);
      setItems(r1.data || []);
      setOverrides(r2.data || []);
    } catch (e) {
      toast.error("Failed to load content requests");
    }
  };
  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const undoOverride = async (id, section) => {
    if (!window.confirm(`Undo the live copy change for "${section}"? The previous copy will return on next page load.`)) return;
    try {
      await axios.delete(`${API}/admin/seo/content-overrides/${id}`, { headers: authHeaders() });
      toast.success("Override removed. Refresh the public page to see the original copy.");
      refresh();
    } catch (e) {
      toast.error("Undo failed");
    }
  };

  const decide = async (id, decision, section) => {
    const notes = notesMap[id] || "";
    if (decision === "reject" && !notes.trim()) {
      toast.error("Please add notes explaining why before rejecting");
      return;
    }
    if (!window.confirm(`${decision === "approve" ? "Approve" : "Reject"} the copy change for "${section}"?`)) return;
    try {
      await axios.post(
        `${API}/admin/seo/content-requests/${id}/decide`,
        { decision, notes },
        { headers: authHeaders() }
      );
      toast.success(`Request ${decision === "approve" ? "approved and copy is now live on the page" : "rejected"}.`);
      setNotesMap((m) => {
        const copy = { ...m };
        delete copy[id];
        return copy;
      });
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Decision failed");
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center justify-between">
          <span>Copy Change Requests from SEO Agency</span>
          <div className="flex gap-1">
            {["pending", "approved", "rejected", "all"].map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                className={`text-xs px-3 py-1 rounded ${
                  filter === s ? "bg-[#F97316] text-white" : "bg-[#F1F5F9] text-[#64748B] hover:bg-[#E2E8F0]"
                }`}
                data-testid={`cr-filter-${s}`}
              >
                {s}
              </button>
            ))}
          </div>
        </CardTitle>
        <p className="text-xs text-[#64748B] mt-1">
          Body-copy edits to the 5 gated pages land here for your approval. Edits to all other pages auto-apply instantly — you only see them in the Audit Log + the green &quot;Live Overrides&quot; panel below. Click <strong>Undo</strong> on any override to roll back.
        </p>
      </CardHeader>
      <CardContent>
        {overrides.length > 0 && (
          <div className="mb-6 p-3 bg-green-50 border border-green-200 rounded">
            <div className="text-xs font-semibold text-green-900 mb-2">
              Active Live Overrides ({overrides.length}) — currently applied to the public site
            </div>
            <div className="space-y-2">
              {overrides.map((o) => (
                <div key={o.id} className="flex items-start justify-between gap-3 bg-white rounded p-2 border border-green-100" data-testid={`override-row-${o.id}`}>
                  <div className="text-xs flex-1 min-w-0">
                    <div className="font-mono text-[#0F172A]">{o.page_path} · {o.section}</div>
                    <div className="text-[#64748B] line-through truncate">{o.current_text}</div>
                    <div className="text-green-700 font-semibold truncate">→ {o.replacement_text}</div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => undoOverride(o.id, o.section)}
                    className="text-red-600 border-red-300 hover:bg-red-50 shrink-0"
                    data-testid={`override-undo-${o.id}`}
                  >
                    Undo
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
        {items.length === 0 ? (
          <p className="text-sm text-[#94A3B8] py-6 text-center">
            {filter === "pending" ? "No pending requests. You're all caught up. ✓" : `No ${filter} requests.`}
          </p>
        ) : (
          <div className="space-y-4">
            {items.map((r) => (
              <div key={r.id} className="border border-[#E2E8F0] rounded p-4" data-testid={`admin-cr-${r.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="text-xs text-[#64748B]">
                      From <strong>{r.editor_name}</strong> ({r.editor_email}) ·{" "}
                      {new Date(r.submitted_at).toLocaleString()}
                    </div>
                    <div className="font-mono text-sm text-[#0F172A] mt-1">
                      {r.page_path} <span className="text-[#94A3B8]">·</span> {r.section}
                    </div>
                  </div>
                  <Badge
                    className={
                      r.status === "approved"
                        ? "bg-green-100 text-green-800 hover:bg-green-100"
                        : r.status === "rejected"
                        ? "bg-red-100 text-red-800 hover:bg-red-100"
                        : "bg-amber-100 text-amber-800 hover:bg-amber-100"
                    }
                  >
                    {r.status}
                  </Badge>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                  <div className="bg-amber-50 p-2 rounded border border-amber-100">
                    <div className="font-semibold text-amber-800 mb-1">Current copy</div>
                    <div className="whitespace-pre-wrap">{r.current_text}</div>
                  </div>
                  <div className="bg-green-50 p-2 rounded border border-green-100">
                    <div className="font-semibold text-green-800 mb-1">Proposed copy</div>
                    <div className="whitespace-pre-wrap">{r.proposed_text}</div>
                  </div>
                </div>

                <div className="text-xs mt-3 p-2 bg-[#F8FAFC] rounded">
                  <strong>SEO reasoning:</strong> {r.reason}
                </div>

                {(r.compliance_replacements || []).length > 0 && (
                  <div className="text-xs mt-2 p-2 bg-orange-50 border border-orange-200 rounded">
                    <strong className="text-orange-800">
                      Auto-fixes applied during compliance scrub ({r.compliance_replacements.length}):
                    </strong>
                    <ul className="mt-1 list-disc list-inside text-orange-700">
                      {r.compliance_replacements.map((rep, i) => (
                        <li key={i}>
                          <code>&quot;{rep.original}&quot;</code> → <code>&quot;{rep.replacement}&quot;</code>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {r.status === "pending" ? (
                  <div className="mt-3 space-y-2">
                    <textarea
                      className="w-full text-xs border border-[#E2E8F0] rounded p-2"
                      rows={2}
                      placeholder="Optional notes for Approve, REQUIRED for Reject"
                      value={notesMap[r.id] || ""}
                      onChange={(e) => setNotesMap({ ...notesMap, [r.id]: e.target.value })}
                      data-testid={`admin-cr-notes-${r.id}`}
                    />
                    <div className="flex gap-2 justify-end">
                      <Button
                        size="sm"
                        onClick={() => decide(r.id, "approve", r.section)}
                        className="bg-green-600 hover:bg-green-700 text-white"
                        data-testid={`admin-cr-approve-${r.id}`}
                      >
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => decide(r.id, "reject", r.section)}
                        className="border-red-300 text-red-600 hover:bg-red-50"
                        data-testid={`admin-cr-reject-${r.id}`}
                      >
                        Reject
                      </Button>
                    </div>
                  </div>
                ) : r.decision_notes ? (
                  <div className="text-xs mt-3 p-2 bg-[#F1F5F9] rounded">
                    <strong>Your notes:</strong> {r.decision_notes}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
